import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import StreamingMaterials from './StreamingMaterials';
import StreamingChapters from './StreamingChapters';
import StreamingQnA from './StreamingQnA';
import StreamingFAQ from './StreamingFAQ';
import { apiClient } from '../../../services';
import { getLectureProgress, updateLectureProgress, parseProgressTime } from '../../../services/lectureApi';

// Vimeo Player 타입 정의
declare global {
  interface Window {
    Vimeo: any;
  }
}

const StreamingIndex: React.FC = () => {
  const { lectureId } = useParams<{ lectureId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'chapters' | 'materials' | 'faq' | 'qna'>('chapters');
  const [isScrolled, setIsScrolled] = useState(false);
  const [lectureData, setLectureData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [playerReady, setPlayerReady] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0); // 진행률 퍼센트
  const [showCompletionModal, setShowCompletionModal] = useState(false); // 강의 완료 모달 상태
  const [lectureCompleted, setLectureCompleted] = useState(false); // 강의 완료 여부
  const [initialProgressSeconds, setInitialProgressSeconds] = useState(0); // 초기 진행률 (초 단위)
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const mountedRef = useRef(true); // 컴포넌트 마운트 상태 추적
  const progressUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null); // 5초마다 업데이트 인터벌
  const lastProgressUpdateRef = useRef(0); // 마지막 업데이트한 진행률

  // 컴포넌트 마운트 시 mounted 상태 설정
  useEffect(() => {
    mountedRef.current = true;
    
    // 부모 창으로부터 세션 정보 받기
    const handleMessage = (event: MessageEvent) => {
      // 보안을 위해 origin 확인
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'SESSION_INFO') {
        console.log('Received session info:', event.data);
        
        // 받은 토큰을 localStorage에 저장 (필요한 경우)
        if (event.data.token) {
          localStorage.setItem('access_token', event.data.token);
          console.log('Session token updated in streaming window');
          console.log('Token first 20 chars:', event.data.token.substring(0, 20) + '...');
        } else {
          // 기존 토큰 확인
          const existingToken = localStorage.getItem('access_token');
          console.log('No token in session info, existing token:', !!existingToken);
        }
        
        // 강의 데이터도 함께 받았다면 설정
        if (event.data.lectureData) {
          console.log('Received lecture data from parent:', event.data.lectureData);
          setLectureData(event.data.lectureData);
          setLoading(false); // 로딩 완료
          
          // 진행률 정보도 함께 받았다면 설정
          if (event.data.progressData) {
            console.log('Received progress data from parent:', event.data.progressData);
            const progressSeconds = parseProgressTime(event.data.progressData.total_time_watched || '0h0m0s');
            setInitialProgressSeconds(progressSeconds);
            
            if (event.data.progressData.completion_rate) {
              setProgressPercentage(Math.floor(event.data.progressData.completion_rate));
            }
          }
        }
      }
    };
    
    // 창 닫기 이벤트 처리 - 진행률 업데이트
    const handleBeforeUnload = () => {
      if (playerRef.current && lectureId && mountedRef.current) {
        playerRef.current.getCurrentTime().then((currentTime: number) => {
          if (currentTime > lastProgressUpdateRef.current) {
            // 비동기이지만 브라우저가 요청을 보낼 수 있도록 시도
            updateLectureProgress(parseInt(lectureId), Math.floor(currentTime));
          }
        }).catch((error: any) => {
          console.error('Error getting current time on unload:', error);
        });
      }
    };
    
    // 메시지 리스너 등록
    window.addEventListener('message', handleMessage);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      mountedRef.current = false;
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // 진행률 업데이트 인터벌 정리
      if (progressUpdateIntervalRef.current) {
        clearInterval(progressUpdateIntervalRef.current);
      }
    };
  }, [lectureId]);

  // 진행률 업데이트 함수
  const updateProgress = async (currentTimeSeconds: number) => {
    if (!lectureId || !mountedRef.current) return;
    
    try {
      // 토큰 확인
      const token = localStorage.getItem('access_token');
      console.log('Progress update - Token exists:', !!token);
      if (token) {
        console.log('Token first 20 chars:', token.substring(0, 20) + '...');
      }
      
      // 진행률이 증가했을 때만 업데이트
      if (currentTimeSeconds > lastProgressUpdateRef.current) {
        // 현재 진행률이 초기 진행률보다 낮으면 서버 업데이트 스킵
        if (currentTimeSeconds < initialProgressSeconds) {
          console.log(`진행률이 초기값(${initialProgressSeconds}초)보다 낮음(${Math.floor(currentTimeSeconds)}초) - 서버 업데이트 스킵`);
          // UI 상태만 업데이트 (서버 업데이트는 하지 않음)
          lastProgressUpdateRef.current = Math.floor(currentTimeSeconds);
        } else {
          // 서버에 진행률 업데이트
          await updateLectureProgress(parseInt(lectureId), Math.floor(currentTimeSeconds));
          lastProgressUpdateRef.current = Math.floor(currentTimeSeconds);
          console.log(`진행률 업데이트됨: ${Math.floor(currentTimeSeconds)}초`);
        }
        
        // 진행률 퍼센트 계산 (UI는 항상 업데이트)
        // lectureData.duration이 없으면 서버에서 받은 total_time_expected 사용
        let totalDuration = lectureData?.duration;
        if (!totalDuration && lectureData?.total_time_expected) {
          totalDuration = parseProgressTime(lectureData.total_time_expected);
        }
        
        if (totalDuration) {
          const percentage = Math.min((currentTimeSeconds / totalDuration) * 100, 100);
          setProgressPercentage(Math.floor(percentage));
          
          // 백업용 완료 체크 (비디오 ended 이벤트가 안 올 경우를 대비)
          // 98% 이상 시청 시 완료로 간주 (ended 이벤트가 주 완료 처리)
          if (percentage >= 98 && !lectureCompleted) {
            console.log('백업 완료 체크: 98% 이상 도달');
            setLectureCompleted(true);
            setShowCompletionModal(true);
          }
        }
      }
    } catch (error) {
      console.error('진행률 업데이트 실패:', error);
    }
  };

  // 강의 데이터 가져오기
  useEffect(() => {
    const fetchLectureData = async () => {
      if (lectureId && !lectureData) { // 강의 데이터가 없을 때만 API 호출
        try {
          setLoading(true);
          console.log('API 요청:', `/api/lectures/detail/${lectureId}`);
          
          const { data } = await apiClient.get<{
            code: number; status: string; message: string; data: any
          }>(`/api/lectures/detail/${lectureId}`);
          console.log('API 응답:', data);
          
          if (data.code === 200) {
            console.log('API에서 강의 데이터 설정:', data.data);
            setLectureData(data.data);
          } else {
            console.error('API error:', data.message);
            // 에러 발생 시 기본 데이터 설정
            setLectureData({
              id: lectureId,
              title: `강의 ${lectureId}`,
              introduction: '강의 소개',
              vimeo_id: '1093667661', // 기본 테스트 비디오
              lecturer_name: '교수명'
            });
          }
        } catch (error) {
          console.error('Error fetching lecture data:', error);
          // 에러 발생 시 기본 데이터 설정
          setLectureData({
            id: lectureId,
            title: `강의 ${lectureId}`,
            introduction: '강의 소개',
            vimeo_id: '1093667661', // 기본 테스트 비디오
            lecturer_name: '교수명'
          });
        } finally {
          setLoading(false);
        }
      }
    };

    // 부모 창에서 데이터를 받지 못했을 때만 API 호출
    const timer = setTimeout(() => {
      if (!lectureData) {
        console.log('부모 창에서 데이터를 받지 못했으므로 API 호출');
        fetchLectureData();
      }
    }, 1000); // 1초 대기 후 API 호출

    return () => clearTimeout(timer);
  }, [lectureId, lectureData]); // lectureData 의존성 추가

  // 페이지 제목 설정
  useEffect(() => {
    if (lectureData?.title) {
      document.title = `투비닥터 캠퍼스 - ${lectureData.title}`;
    } else {
      document.title = '투비닥터 캠퍼스 - 젊은 의사의 학습 러닝메이트';
    }
    
    // 컴포넌트 언마운트 시 원래 제목으로 복원
    return () => {
      document.title = '투비닥터 캠퍼스 - 젊은 의사의 학습 러닝메이트';
    };
  }, [lectureData]);

  // 강의 설문하러 가기 (새창으로 설문 페이지 열기)
  const goToSurvey = () => {
    if (lectureId) {
      // 새창으로 설문 페이지 열기
      const surveyUrl = `/mypage/survey/${lectureId}`;
      window.open(surveyUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    }
    
    // 모달 닫기
    setShowCompletionModal(false);
  };

  // 비디오 시간 이동 함수 (챕터 클릭 시 진행률 업데이트 포함)
  const seekToTime = async (seconds: number) => {
    console.log('Seeking to time:', seconds, 'Player ready:', playerReady, 'Player exists:', !!playerRef.current);
    
    if (!playerRef.current) {
      console.warn('Player not available for seeking');
      return;
    }
    
    if (!playerReady) {
      console.warn('Player not ready for seeking, waiting...');
      // 플레이어가 준비되면 시간 이동 실행
      const checkPlayerReady = setInterval(() => {
        if (playerReady && playerRef.current && mountedRef.current) {
          clearInterval(checkPlayerReady);
          console.log('Player ready, seeking to:', seconds);
          playerRef.current.setCurrentTime(seconds).then(() => {
            if (mountedRef.current) {
              console.log(`Video seeked to ${seconds} seconds`);
              // 챕터 클릭으로 시간 이동 시 진행률 업데이트
              updateProgress(seconds);
            }
          }).catch((error: any) => {
            if (mountedRef.current) {
              console.error('Error seeking video:', error);
            }
          });
        }
      }, 100);
      
      // 10초 후 타임아웃
      setTimeout(() => {
        clearInterval(checkPlayerReady);
        console.warn('Player ready timeout');
      }, 10000);
      
      return;
    }
    
    if (playerRef.current && playerReady && mountedRef.current) {
      try {
        await playerRef.current.setCurrentTime(seconds);
        if (mountedRef.current) {
          console.log(`Video seeked to ${seconds} seconds`);
          // 챕터 클릭으로 시간 이동 시 진행률 업데이트
          await updateProgress(seconds);
        }
      } catch (error) {
        if (mountedRef.current) {
          console.error('Error seeking video:', error);
        }
      }
    }
  };

  // 5초마다 진행률 업데이트 시작
  const startProgressUpdateInterval = () => {
    // 기존 인터벌이 있다면 정리
    if (progressUpdateIntervalRef.current) {
      clearInterval(progressUpdateIntervalRef.current);
    }
    
    progressUpdateIntervalRef.current = setInterval(() => {
      if (playerRef.current && mountedRef.current) {
        playerRef.current.getCurrentTime().then((currentTime: number) => {
          if (mountedRef.current) {
            // 진행률이 실제로 증가했을 때만 업데이트
            if (currentTime > lastProgressUpdateRef.current + 1) { // 1초 이상 차이날 때만
              updateProgress(currentTime);
            }
          }
        }).catch((error: any) => {
          console.error('Error getting current time for progress update:', error);
        });
      }
    }, 5000); // 5초마다 실행
  };

  // 저장된 진행률로 비디오 시작 지점 복원
  const restoreVideoProgress = async () => {
    if (!lectureId || !playerRef.current || !mountedRef.current) return;
    
    try {
      console.log('진행률 복원 시작...');
      const progressData = await getLectureProgress(parseInt(lectureId));
      
      if (progressData.code === 200 && progressData.data.total_time_watched) {
        const progressSeconds = parseProgressTime(progressData.data.total_time_watched);
        
        // 초기 진행률 저장
        setInitialProgressSeconds(progressSeconds);
        console.log(`초기 진행률 설정: ${progressSeconds}초 (${progressData.data.total_time_watched})`);
        
        // completion_rate가 있으면 UI에 반영
        if (progressData.data.completion_rate) {
          setProgressPercentage(Math.floor(progressData.data.completion_rate));
          console.log(`초기 완료율 설정: ${Math.floor(progressData.data.completion_rate)}%`);
        }
        
        // 진행률이 5초 이상인 경우에만 해당 지점으로 이동
        if (progressSeconds >= 5) {
          console.log(`저장된 진행률로 ${progressSeconds}초 지점으로 이동`);
          
          try {
            await playerRef.current.setCurrentTime(progressSeconds);
            
            // 상태 업데이트
            lastProgressUpdateRef.current = progressSeconds;
            
            console.log(`비디오 진행률 복원 완료: ${progressSeconds}초`);
          } catch (error) {
            console.error('비디오 시간 이동 실패:', error);
          }
        } else {
          console.log('진행률이 5초 미만이므로 처음부터 시작');
          // 5초 미만이라도 상태는 업데이트
          lastProgressUpdateRef.current = progressSeconds;
        }
      } else {
        console.log('저장된 진행률이 없음');
        setInitialProgressSeconds(0);
      }
    } catch (error) {
      console.error('진행률 복원 실패:', error);
      setInitialProgressSeconds(0);
    }
  };

  // 탭 변경 핸들러
  const handleTabChange = (tab: 'chapters' | 'materials' | 'faq' | 'qna') => {
    setActiveTab(tab);
  };

  // 스크롤 이벤트 감지
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Vimeo Player 초기화
  useEffect(() => {
    if (!lectureData) return; // 강의 데이터가 없으면 실행하지 않음
    
    console.log('Attempting to initialize player, mounted:', mountedRef.current);

    // 약간의 지연을 두고 초기화 (DOM이 완전히 준비되도록)
    const initTimeout = setTimeout(() => {
      // Vimeo Player SDK 스크립트 로드
      if (!window.Vimeo) {
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = initializePlayer;
        document.head.appendChild(script);
      } else {
        initializePlayer();
      }
    }, 100);

    function initializePlayer() {
      if (videoRef.current && window.Vimeo && lectureData?.vimeo_id) {
        console.log('Initializing Vimeo player with ID:', lectureData.vimeo_id);
        
        try {
          // 기존 플레이어가 있다면 먼저 제거
          if (playerRef.current) {
            try {
              playerRef.current.destroy();
            } catch (error) {
              console.warn('Error destroying existing player:', error);
            }
            playerRef.current = null;
            setPlayerReady(false);
          }
          
          // 비디오 컨테이너 초기화
          if (videoRef.current) {
            videoRef.current.innerHTML = '';
          }
          
          // 약간의 지연을 두고 새 플레이어 생성
          setTimeout(() => {
            if (videoRef.current && mountedRef.current) {
              playerRef.current = new window.Vimeo.Player(videoRef.current, {
                id: lectureData.vimeo_id,
                width: '100%',
                height: '100%',
                responsive: true
              });

              console.log('Vimeo player initialized successfully');

              // 플레이어 준비 완료 이벤트
              playerRef.current.on('loaded', () => {
                if (mountedRef.current) {
                  console.log('Player loaded event fired');
                  setTimeout(() => {
                    if (mountedRef.current) {
                      setPlayerReady(true);
                      console.log('Player is ready');
                      
                      // 5초마다 진행률 업데이트 시작
                      startProgressUpdateInterval();
                      
                      // 저장된 진행률로 비디오 시작 지점 복원
                      setTimeout(() => {
                        restoreVideoProgress();
                      }, 500); // 추가 0.5초 대기 후 진행률 복원
                    }
                  }, 500); // 500ms 대기 후 ready 상태 설정
                }
              });

              // 추가 안전장치 - ready 이벤트도 처리
              playerRef.current.on('ready', () => {
                if (mountedRef.current) {
                  console.log('Player ready event fired');
                  setTimeout(() => {
                    if (mountedRef.current) {
                      setPlayerReady(true);
                      console.log('Player is ready (from ready event)');
                      
                      // 5초마다 진행률 업데이트 시작
                      startProgressUpdateInterval();
                      
                      // 저장된 진행률로 비디오 시작 지점 복원
                      setTimeout(() => {
                        restoreVideoProgress();
                      }, 500); // 추가 0.5초 대기 후 진행률 복원
                    }
                  }, 200);
                }
              });

              // 이벤트 리스너 추가
              playerRef.current.on('play', () => {
                if (mountedRef.current) {
                  console.log('Video started playing');
                  // 재생 시작 시 5초마다 업데이트 인터벌 시작
                  startProgressUpdateInterval();
                }
              });

              playerRef.current.on('pause', () => {
                if (mountedRef.current) {
                  console.log('Video paused');
                  // 일시정지 시 현재 진행률 즉시 업데이트
                  if (playerRef.current) {
                    playerRef.current.getCurrentTime().then((currentTime: number) => {
                      if (mountedRef.current) {
                        updateProgress(currentTime);
                      }
                    }).catch((error: any) => {
                      console.error('Error getting current time on pause:', error);
                    });
                  }
                }
              });

              // 비디오 종료 이벤트 - 강의 완료 모달 표시
              playerRef.current.on('ended', () => {
                if (mountedRef.current) {
                  console.log('Video ended - showing completion modal');
                  // 비디오가 끝나면 즉시 강의 완료 처리
                  if (!lectureCompleted) {
                    setLectureCompleted(true);
                    setShowCompletionModal(true);
                    
                    // 진행률을 100%로 설정
                    setProgressPercentage(100);
                    
                    // 최종 진행률 서버에 업데이트
                    if (lectureData?.duration) {
                      updateProgress(lectureData.duration);
                    }
                  }
                }
              });

              playerRef.current.on('ended', () => {
                if (mountedRef.current) {
                  console.log('Video ended');
                  // 영상 종료 시 최종 진행률 업데이트
                  if (playerRef.current) {
                    playerRef.current.getDuration().then((duration: number) => {
                      if (mountedRef.current) {
                        updateProgress(duration);
                      }
                    }).catch((error: any) => {
                      console.error('Error getting duration on end:', error);
                    });
                  }
                }
              });

              playerRef.current.on('timeupdate', (data: any) => {
                if (mountedRef.current) {
                  // 시간 업데이트 시 현재 시간 정보를 참조값에 저장
                  
                  // 진행률 퍼센트 계산
                  // lectureData.duration이 없으면 서버에서 받은 total_time_expected 사용
                  let totalDuration = lectureData?.duration;
                  if (!totalDuration && lectureData?.total_time_expected) {
                    totalDuration = parseProgressTime(lectureData.total_time_expected);
                  }
                  
                  if (totalDuration) {
                    const percentage = Math.min((data.seconds / totalDuration) * 100, 100);
                    setProgressPercentage(Math.floor(percentage));
                  }
                }
              });

              // 에러 처리
              playerRef.current.on('error', (error: any) => {
                if (mountedRef.current) {
                  console.error('Vimeo player error:', error);
                  setPlayerReady(false);
                }
              });
            }
          }, 100);

        } catch (error) {
          console.error('Error initializing player:', error);
          setPlayerReady(false);
        }
      } else {
        console.log('Player initialization skipped:', {
          hasVideoRef: !!videoRef.current,
          hasVimeo: !!window.Vimeo,
          hasLectureData: !!lectureData,
          vimeoId: lectureData?.vimeo_id,
          isMounted: mountedRef.current
        });
      }
    }

    return () => {
      clearTimeout(initTimeout);
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
          console.log('Player destroyed on unmount');
        } catch (error) {
          console.warn('Error destroying player on unmount:', error);
        }
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lectureData]); // lectureData 의존성 추가

  return (
    <StreamingContainer>
      {loading ? (
        <LoadingContainer>
          <LoadingText>강의 정보를 불러오는 중...</LoadingText>
        </LoadingContainer>
      ) : (
        <>
          {/* 왼쪽 비디오 영역 */}
          <LeftVideoArea>
            {/* 왼쪽 헤더 (로고 + 제목 + 수강률) */}
            <VideoHeader scrolled={isScrolled}>
              <HeaderContent>
                <LeftSection>
                  <Logo>
                    <LogoIcon />
                  </Logo>
                  <CourseInfo>
                    <CourseTitle>{lectureData?.title || '로딩 중...'}</CourseTitle>
                  </CourseInfo>
                </LeftSection>
                <RightSection>
                  <ProgressInfo>수강률 {progressPercentage}%</ProgressInfo>
                </RightSection>
              </HeaderContent>
            </VideoHeader>

            {/* 비디오 플레이어 */}
            <VideoSection>
              <VideoPlayer ref={videoRef} />
            </VideoSection>

            {/* 강의 완료 모달 - 비디오 영역에만 표시 */}
            {showCompletionModal && (
              <VideoCompletionModalOverlay>
                <CompletionModal>
                  <CompletionContent>
                    <TrophyIconContainer>
                      <TrophyIcon>
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M17.3333 8.26562C16.0078 8.26562 14.9333 9.34013 14.9333 10.6656L14.9332 17.599H5.33359C4.00811 17.599 2.93359 18.6735 2.93359 19.999C2.93359 34.9583 14.7776 47.1514 29.5997 47.7122V53.599H17.3331C16.0076 53.599 14.9331 54.6735 14.9331 55.999C14.9331 57.3244 16.0076 58.399 17.3331 58.399H46.6663C47.9918 58.399 49.0663 57.3244 49.0663 55.999C49.0663 54.6735 47.9918 53.599 46.6663 53.599H34.3997V47.7122C49.2219 47.1515 61.066 34.9584 61.066 19.999C61.066 18.6735 59.9915 17.599 58.666 17.599H49.0664L49.0665 10.6657C49.0665 10.0291 48.8136 9.41867 48.3636 8.96858C47.9135 8.51849 47.303 8.26562 46.6665 8.26562H17.3333ZM49.0664 22.399L49.0663 24.7985C49.0663 28.5386 48.9793 30.889 48.7557 32.742L48.7514 32.7779C48.4789 34.7812 47.9985 36.5845 47.2635 38.2178C52.1391 34.484 55.4724 28.8373 56.1419 22.399H49.0664ZM16.7359 38.2177C16.0158 36.6016 15.547 34.8058 15.2767 32.7704L15.2749 32.7567C15.0202 30.7512 14.9331 28.3671 14.9331 24.7984L14.9331 22.399H7.85765C8.52718 28.8372 11.8604 34.4839 16.7359 38.2177ZM19.7331 24.7984L19.7332 13.0656H44.2665L44.2663 24.7984C44.2663 28.4945 44.178 30.6008 43.9925 32.1488C43.5486 35.3925 42.5015 37.5938 40.7207 39.3766C38.4481 41.6222 35.2459 42.9317 31.9997 42.9317C28.7841 42.9317 25.5229 41.5938 23.2787 39.3765C21.4974 37.5932 20.4796 35.4791 20.0358 32.1452C19.8217 30.4559 19.7331 28.3236 19.7331 24.7984Z" fill="var(--Primary-Normal, #448181)"/>
                        </svg>
                      </TrophyIcon>
                    </TrophyIconContainer>
                    
                    <CompletionTextContainer>
                      <CompletionTitle>강의 시청을 완료하였습니다.</CompletionTitle>
                      <CompletionSubtext>
                        강의 설문을 통해 강의에 대한 여러분의 소중한 의견을 남겨주세요.
                      </CompletionSubtext>
                    </CompletionTextContainer>
                  </CompletionContent>
                  
                  {/* <CompletionDivider /> */}
                  
                  <CompletionButtonSection>
                    <SurveyButton onClick={goToSurvey}>
                      강의 설문하러 가기
                    </SurveyButton>
                  </CompletionButtonSection>
                </CompletionModal>
              </VideoCompletionModalOverlay>
            )}
          </LeftVideoArea>

          {/* 오른쪽 사이드 패널 */}
          <RightSidePanel>
            {/* 오른쪽 헤더 (탭 네비게이션) */}
            <SideHeader scrolled={isScrolled}>
              <TabNavigation activeTab={activeTab}>
                <Tab 
                  active={activeTab === 'chapters'} 
                  onClick={() => handleTabChange('chapters')}
                >
                  챕터
                </Tab>
                <Tab 
                  active={activeTab === 'materials'} 
                  onClick={() => handleTabChange('materials')}
                >
                  강의자료
                </Tab>
                <Tab 
                  active={activeTab === 'faq'} 
                  onClick={() => handleTabChange('faq')}
                >
                  FAQ
                </Tab>
                <Tab 
                  active={activeTab === 'qna'} 
                  onClick={() => handleTabChange('qna')}
                >
                  Q&A
                </Tab>
              </TabNavigation>
            </SideHeader>

            {/* 탭 컨텐츠 */}
            <TabContent>
              {activeTab === 'chapters' && <StreamingChapters onSeekToTime={seekToTime} lectureId={lectureId} />}
              {activeTab === 'materials' && <StreamingMaterials />}
              {activeTab === 'faq' && <StreamingFAQ lectureId={lectureId} />}
              {activeTab === 'qna' && <StreamingQnA lectureId={lectureId} lectureName={lectureData?.title} />}
            </TabContent>
          </RightSidePanel>
        </>
      )}
    </StreamingContainer>
  );
};

/* 전체 컨테이너 */
const StreamingContainer = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  background: var(--Static-White, white);
  display: flex;
  overflow: hidden; /* 전체 스크롤 완전 제거 */
`;

/* 왼쪽 비디오 영역 */
const LeftVideoArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative; /* 모달 위치 지정을 위해 relative 추가 */
`;

const VideoHeader = styled.div<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 480px; /* 사이드 패널 너비만큼 제외 */
  height: 80px;
  display: flex;
  align-items: center;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.88)'};
  backdrop-filter: blur(32px);
  z-index: 1000;
  transition: all 0.3s ease;
`;

const HeaderContent = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const LogoIcon = styled.div`
  width: 24px;
  height: 24px;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    background-image: url("data:image/svg+xml,%3Csvg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.1948 8.5267C21.1403 12.1274 19.7384 15.7118 16.9893 18.4612C14.7105 20.74 11.859 22.094 8.8953 22.5196C11.1549 19.7417 12.387 16.2914 12.387 12.6651C12.387 12.3549 12.3773 12.0444 12.3582 11.7371C14.928 9.64084 18.0551 8.57143 21.1948 8.5267Z' fill='%23448181'/%3E%3Cpath d='M11.107 12.6651C11.107 12.7387 11.107 12.8123 11.1037 12.8859C11.046 16.6881 9.51638 20.1287 7.05511 22.6667L7.05187 22.6635C4.54262 20.0774 3 16.5536 3 12.6651C3 8.77665 4.54262 5.25282 7.05511 2.66675C9.56435 5.25282 11.107 8.77953 11.107 12.6651Z' fill='%23448181'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const CourseInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
`;

const CourseTitle = styled.div`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  word-wrap: break-word;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const ProgressInfo = styled.div`
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  word-wrap: break-word;
  white-space: nowrap;
`;

const VideoSection = styled.div`
  flex: 1;
  margin-top: 80px; /* 헤더 높이만큼 마진 */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const VideoPlayer = styled.div`
  width: 100%;
  height: 830px;
  position: relative;
  background: #000;
  
  /* Vimeo Player가 정확히 영역에 맞도록 */
  > div {
    width: 100% !important;
    height: 100% !important;
  }
  
  iframe {
    width: 100% !important;
    height: 100% !important;
    border: none;
  }
`;

/* 오른쪽 사이드 패널 */
const RightSidePanel = styled.div`
  width: 480px;
  display: flex;
  flex-direction: column;
  background: var(--Static-White, white);
  position: relative;
  overflow: hidden; /* 스크롤 완전 차단 */
`;

const SideHeader = styled.div<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  height: 80px;
  display: flex;
  align-items: center;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.88)'};
  backdrop-filter: blur(32px);
  z-index: 1000;
  transition: all 0.3s ease;
  padding: 0 16px;
`;

const TabNavigation = styled.div<{ activeTab: 'chapters' | 'materials' | 'faq' | 'qna' }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 100%;
  position: relative;
  border-bottom: 1px solid rgba(112, 115, 124, 0.08);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 25%; /* 탭 하나 크기 */
    height: 2px;
    background: var(--Primary-Strong, #296768);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(${props => {
      // activeTab 값에 따라 바 위치 계산
      const tabIndex = ['chapters', 'materials', 'faq', 'qna'].indexOf(props.activeTab);
      return tabIndex * 100;
    }}%);
  }
`;

const Tab = styled.div<{ active: boolean }>`
  flex: 1;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${props => props.active 
    ? 'var(--Primary-Strong, #296768)' 
    : 'var(--Label-Assistive, rgba(55, 56, 60, 0.28))'
  };
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--Primary-Strong, #296768);
  }
`;

const TabContent = styled.div`
  flex: 1;
  margin-top: 80px; /* 헤더 높이만큼 마진 */
  width: 100%;
  height: calc(100vh - 80px); /* 헤더 제외한 정확한 높이 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: hidden; /* 내부 스크롤도 제거 */
`;

/* 로딩 상태 */
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  background: var(--Static-White, white);
`;

const LoadingText = styled.div`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

/* 강의 완료 모달 스타일 - 비디오 영역 전용 */
const VideoCompletionModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--Material-Dimmer, rgba(23, 23, 25, 0.52));
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const CompletionModal = styled.div`
  width: 400px;
  max-width: 1060px;
  min-width: 320px;
  max-height: 760px;
  background: var(--Background-Elevated-Normal, white);
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
`;

const CompletionContent = styled.div`
  align-self: stretch;
  padding-top: 32px;
  padding-bottom: 16px;
  padding-left: 24px;
  padding-right: 24px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  display: flex;
`;

const TrophyIconContainer = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const TrophyIcon = styled.div`
  height: 64px;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const CompletionTextContainer = styled.div`
  width: 300px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: flex;
`;

const CompletionTitle = styled.div`
  color: var(--Label-Strong, black);
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  word-wrap: break-word;
`;

const CompletionSubtext = styled.div`
  align-self: stretch;
  text-align: center;
  color: var(--Label-Strong, black);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const CompletionButtonSection = styled.div`
  align-self: stretch;
  padding: 24px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: flex;
`;

const SurveyButton = styled.button`
  align-self: stretch;
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  background: var(--Primary-Normal, #448181);
  overflow: hidden;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
  transition: all 0.2s ease;
  
  color: var(--Static-White, white);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  
  &:hover {
    background: var(--Primary-Heavy, #3a6f6f);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

export default StreamingIndex;
